/**
 * ObsidianGraphView — vibe-designer 1.175.0 Phase 5β3.
 *
 * Per-service viz on the OBSIDIAN detail panel. Calls
 * GET /api/knowledge/services/obsidian/graph → renders react-force-graph-2d
 * with one node per .md page + one edge per [[wikilink]] reference.
 *
 * Mounted BELOW the existing Phase 5β1 ObsidianPageList in
 * KnowledgeServiceDetail (both viz stack — the list shows page metadata,
 * the graph shows page-to-page connectivity).
 *
 * Renderer: shares the `_KnowledgeSigmaCanvas` (Sigma/WebGL) 2D chunk with
 * MIND + CORTEX — node size by degree, group color, labels.
 */
import { useEffect, Suspense, lazy } from 'react';
import { Loader2, RefreshCw, Network } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import type { ObsidianGraphNode, ObsidianGraphEdge } from '@/services/knowledgeApi';

// Reuse the shared Sigma 2D canvas — same {nodes, edges} shape, same chunk.
// _KnowledgeSigmaCanvas group-colors nodes by their `group` field, which works
// identically for OBSIDIAN's 'Page' / 'Orphan' groups.
const ForceGraphCanvas = lazy(() => import('./_KnowledgeSigmaCanvas'));

export function ObsidianGraphView() {
  const graph = useKnowledgeStore((s) => s.obsidianGraph);
  const isFetching = useKnowledgeStore((s) => s.isFetchingObsidianGraph);
  const error = useKnowledgeStore((s) => s.obsidianGraphError);
  const fetchObsidianGraph = useKnowledgeStore((s) => s.fetchObsidianGraph);
  // 1.191.0 Phase 7.6.1 — re-fetch on SSE ingest event so backlink graph
  // topology stays consistent with ObsidianPageList in the same panel.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);

  useEffect(() => {
    void fetchObsidianGraph();
  }, [fetchObsidianGraph, lastIngestAt]);

  const orphanCount = graph?.nodes.filter((n) => n.group === 'Orphan').length ?? 0;

  return (
    <section className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary flex items-center gap-1.5">
          <Network className="w-3 h-3" />
          Backlink graph
          {graph && graph.nodes.length > 0 && (
            <span className="ml-2 text-vibe-text-secondary tabular-nums">
              {graph.nodes.length} node{graph.nodes.length === 1 ? '' : 's'} · {graph.edges.length} link{graph.edges.length === 1 ? '' : 's'}
              {orphanCount > 0 && (
                <span className="ml-1 text-vibe-warning">· {orphanCount} orphan{orphanCount === 1 ? '' : 's'}</span>
              )}
            </span>
          )}
        </h2>
        <button
          data-testid="obsidian-graph-refresh"
          type="button"
          onClick={() => void fetchObsidianGraph()}
          disabled={isFetching}
          className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
        >
          {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          <span>Refresh</span>
        </button>
      </header>

      {error && (
        <div
          data-testid="obsidian-graph-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && graph && graph.error && (
        <div
          data-testid="obsidian-graph-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {graph.error}
        </div>
      )}

      {!error && graph && !graph.error && graph.nodes.length === 0 && (
        <div
          data-testid="obsidian-graph-empty"
          className="text-xs text-vibe-text-secondary py-2"
        >
          No wiki pages with backlinks yet. Add `[[wikilink]]` references between pages in SilverBullet to see the graph.
        </div>
      )}

      {!error && graph && !graph.error && graph.nodes.length > 0 && (
        <div className="h-96 w-full bg-vibe-bg rounded overflow-hidden border border-vibe-border">
          <Suspense fallback={<div className="text-xs text-vibe-text-secondary p-2">Loading graph…</div>}>
            <ForceGraphCanvas nodes={graph.nodes} edges={graph.edges} />
          </Suspense>
        </div>
      )}
    </section>
  );
}

// Re-exported for callers that want the strong types.
export type { ObsidianGraphNode, ObsidianGraphEdge };
